import { formatDate } from "utils/dateFormatter";

/**
 * The course, in the words the backend now sends (KIN-1811).
 *
 * This used to hold a client-side map from course key to display name, because `courseName`
 * came back equal to `courseKey` and "MEN_ACWY" would otherwise have reached the screen. The
 * backend now sends "6 in 1 (DTaP/IPV/Hib/HepB)" and the like, so the map is gone rather than
 * left as a silent override: it would have gone stale the next time a course was added, and a
 * course it did not know about would have kept displaying its key.
 *
 * `courseKey` is still what the recall-state endpoints are keyed on — match on that, show this.
 */
export const formatCourseName = (slot) =>
  slot?.courseName ?? slot?.courseKey ?? "-";

/**
 * Why an outstanding dose is being asked for a second time (KIN-1923).
 *
 * A dose given more than four days before its minimum age or interval is not a valid dose
 * (NIAC Chapter 2 §2.2.6): it stays on the record, it is still reported and still claimed, but
 * it does not fill its slot, and a repeat is due at least four weeks later.
 *
 * Without this sentence the two screens cannot be reconciled — the dose list shows two doses
 * given and the schedule shows dose 2 outstanding — and a clinician who cannot reconcile them
 * distrusts the one telling them a child is short a valid dose.
 *
 * Says why, not when: `earliestOn` already honours the four-week wait and is rendered as a date
 * like any other.
 */
export const formatRepeatReason = (slot) => {
  if (!slot?.repeatOfDisregardedDose) return null;

  const disregarded = slot.disregardedDosesInCourse ?? 0;
  const doses = disregarded === 1 ? "An earlier dose" : `${disregarded} earlier doses`;
  const verb = disregarded === 1 ? "was" : "were";

  return `${doses} of this course ${verb} given too early to count (NIAC Chapter 2 §2.2.6), so this dose is due again.`;
};

// Who gives this dose, for this patient, today. Only said out loud when it is not the
// routine case — labelling every row "we give this" is noise.
const OWNERSHIP_NOTES = {
  OURS_BY_GEOGRAPHY: "Given here in this county; elsewhere it is a school dose",
  OURS_BY_CATCH_UP: "School programme dose — this practice can give it as funded catch-up",
  NOT_OURS: "Given by the school programme, not this practice",
};

export const formatOwnershipNote = (slot) => OWNERSHIP_NOTES[slot?.ownership] ?? null;

/**
 * Past its due date and still not given.
 *
 * The backend has no OVERDUE status — DUE covers "you can give this now" whether it became
 * due yesterday or a year ago. The distinction is presentational and belongs here, but it
 * matters: a dose eight months late is not the same call as one that came due this week.
 */
export const isOverdue = (slot, today = new Date()) => {
  if (slot?.status !== "DUE" || !slot?.dueOn) return false;

  const due = new Date(`${slot.dueOn}T00:00:00`);

  if (Number.isNaN(due.getTime())) return false;

  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return due < startOfToday;
};

const isFutureDate = (isoDate, today) => {
  const date = new Date(`${isoDate}T00:00:00`);

  if (Number.isNaN(date.getTime())) return false;

  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return date > startOfToday;
};

// In the order a clinician works through them: what is late, what can be given today, what
// is waiting on something else, what is coming, what is done.
export const SCHEDULE_GROUPS = [
  {
    id: "overdue",
    title: "Overdue",
    caption: "Past the recommended date and not yet recorded here",
  },
  { id: "due", title: "Due now", caption: "Can be given today" },
  {
    id: "noProduct",
    title: "Due — no product",
    caption:
      "Due for this child, but every product mapped to the course is withdrawn or out of its validity window. Real work, and not work that can be done here.",
  },
  {
    id: "awaiting",
    title: "Waiting on an earlier dose",
    caption: "Becomes due once the dose before it is given",
  },
  { id: "upcoming", title: "Not yet due", caption: null },
  { id: "given", title: "Given", caption: null },
  {
    id: "unrecognised",
    title: "Not recognised",
    caption:
      "The schedule reports a state this version of the app does not know about. Shown rather than dropped, because a hidden dose reads as a dose nobody needs.",
  },
];

/*
 * Statuses that are deliberately not shown at all, as opposed to not yet understood.
 * AGED_OUT is past its upper bound and NOT_APPLICABLE is a conditional slot whose
 * precondition is not met — neither is work, and a conditional slot carries "Do not offer
 * unprompted" in its own notes. Anything outside this list and the groups above is a value
 * added since this version shipped, and falls through to `unrecognised`.
 */
const HIDDEN_STATUSES = ["AGED_OUT", "NOT_APPLICABLE"];

const groupIdFor = (slot, today) => {
  if (slot.status === "GIVEN") return "given";
  if (slot.status === "AWAITING_PREVIOUS_DOSE") return "awaiting";
  if (slot.status === "UPCOMING") return "upcoming";
  if (slot.status === "DUE") return isOverdue(slot, today) ? "overdue" : "due";

  /*
   * Its own group rather than sitting inside "Due now" (KIN-1888). The child is genuinely
   * behind, so it must not read as "not applicable" — but the only action "Due now" offers
   * is a product picker that will not offer the product, which is the exact thing this
   * status exists to stop.
   */
  if (slot.status === "NO_PRODUCT_AVAILABLE") return "noProduct";

  if (HIDDEN_STATUSES.includes(slot.status)) return null;

  /*
   * Anything else is a status added to the response after this version shipped — the eighth
   * value arrived this quarter. Falling through to null dropped the slot from the schedule
   * silently, which is the worst of the options: the dose is still outstanding and the
   * child now looks like they need nothing.
   */
  return "unrecognised";
};

const byDate = (first, second) => {
  const a = first.dueOn ?? first.givenOn ?? "";
  const b = second.dueOn ?? second.givenOn ?? "";

  if (a && b) return a.localeCompare(b);

  return a ? -1 : b ? 1 : 0;
};

/**
 * The schedule arranged the way it is read, soonest first within each group.
 *
 * The backend returns slots grouped by course, which is the wrong order for "what does this
 * child need" — MenACWY due in 2036 arrives before a 2-month dose that is overdue today.
 */
export const groupScheduleSlots = (slots, today = new Date()) => {
  const buckets = new Map(SCHEDULE_GROUPS.map((group) => [group.id, []]));

  (slots ?? []).forEach((slot) => {
    const id = groupIdFor(slot, today);

    if (id) buckets.get(id).push(slot);
  });

  return SCHEDULE_GROUPS.map((group) => ({
    ...group,
    slots: buckets.get(group.id).sort(byDate),
  })).filter((group) => group.slots.length > 0);
};

/**
 * The dates worth putting on the row, and only those.
 *
 * Every slot carries up to four. Showing them all made a two-month-old's row read "Due
 * 15-09-2025 · Can be given from 26-08-2025 · No longer indicated after 15-07-2035", where
 * the middle one is already past and the last is true for another ten years. Neither
 * changes what the clinician does today.
 *
 * `earliestOn` earns its place only while it is still in the future — that is the case
 * where it means "not yet", which is the only case where it is news.
 */
export const formatSlotDates = (slot, today = new Date()) => {
  if (slot?.status === "GIVEN") {
    return slot.givenOn ? [`Given ${formatDate(slot.givenOn)}`] : [];
  }

  const dates = [];

  if (slot?.dueOn) dates.push(`Due ${formatDate(slot.dueOn)}`);

  if (slot?.earliestOn && isFutureDate(slot.earliestOn, today)) {
    dates.push(`Not before ${formatDate(slot.earliestOn)}`);
  }

  return dates;
};

/**
 * What moves off the row and behind the information icon.
 *
 * The upper age bound and the schedule's clinical notes are reference material — needed
 * when someone stops to ask, not while scanning for what is outstanding. Several of the
 * notes are reconciliation remarks written for us rather than for a clinician ("Table 2.2
 * optimal interval is 2 months but Table 2.1 places this visit at 6 months"), which is a
 * second reason not to give them a line each.
 */
export const buildSlotDetail = (slot) => {
  const detail = [];

  if (slot?.latestOn && slot?.status !== "GIVEN") {
    detail.push(`No longer indicated after ${formatDate(slot.latestOn)}`);
  }

  if (slot?.earliestOn) {
    detail.push(`Earliest permissible date ${formatDate(slot.earliestOn)}`);
  }

  if (slot?.notes) detail.push(slot.notes);

  return detail;
};

// A schedule slot names a course, not a product, so recording one means choosing a
// catalogue entry. These are the courses where that choice is unambiguous — matched on the
// catalogue's own `name`, which is what the vaccine picker shows.
//
// Deliberately absent, and each for its own reason:
//   SIX_IN_ONE  two products, Infanrix Hexa and Vaxelis, and the choice is the clinician's
//   RSV         two weight bands, 50mg and 100mg, decided by the child's weight
//   MEN_ACWY    no product in the catalogue at all
//
// PRE_SCHOOL_BOOSTER maps to the 4-in-1: the 4-5 year booster is DTaP/IPV, which is what
// that entry is. Worth revisiting if a distinct pre-school product is ever coded.
const CATALOGUE_NAME_BY_COURSE = {
  FOUR_IN_ONE: "4 in 1",
  PRE_SCHOOL_BOOSTER: "4 in 1",
  PCV13: "PCV",
  MEN_B: "MenB",
  MEN_C: "MenC",
  MMR: "MMR",
  VARICELLA: "Varicella",
  ROTAVIRUS: "Rotavirus",
  HIB_MEN_C: "Hib/MenC",
};

/**
 * The catalogue product to open the form with, or null to let the clinician choose.
 *
 * Null is a normal answer, not a failure: for a 6-in-1 or an RSV dose the product is a
 * clinical decision, and picking one on the clinician's behalf would be worse than leaving
 * the field empty. The dose number is pre-filled either way.
 */
export const findCatalogueVaccineForSlot = (slot, vaccines) => {
  const name = CATALOGUE_NAME_BY_COURSE[slot?.courseKey];

  if (!name) return null;

  return (vaccines ?? []).find((vaccine) => vaccine?.name === name) ?? null;
};

// Only where a dose can actually be given now. A slot waiting on an earlier dose, one not
// yet due, or one already given is not something to record from here.
const RECORDABLE_STATUSES = ["DUE"];

export const isRecordableSlot = (slot) =>
  RECORDABLE_STATUSES.includes(slot?.status) && slot?.ownership !== "NOT_OURS";
